import { useState } from 'react'
import { cleanRights, presetRightsForPosition, RANK, RIGHTS, type AdminWorkshopMember, type Right } from '@wf/shared'
import { PermissionsMatrix } from '../team/PermissionsMatrix'
import { useAdminDeleteUser, useAdminPatchUser } from './useAdmin'

// Super-Admin darf alles vergeben — kein Rang-Gate wie im Chef-Editor (Backend-Pfad filtert nur cleanRights).
const ALL_GRANTABLE: ReadonlySet<Right> = new Set(RIGHTS)

/** Positions-Dropdown des Super-Admins: die V2-Positionen aus RANK; eine abweichende Bestands-Position
 *  (Freitext aus V1-Zeiten) bleibt als eigene Option wählbar, damit nichts still umbenannt wird. */
export function PositionSelect({ value, onChange, disabled }: { value: string; onChange: (p: string) => void; disabled?: boolean }) {
  const positions = Object.keys(RANK)
  const legacy = value && !positions.includes(value) ? value : null
  return (
    <select className="wf-input" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      {legacy && <option value={legacy}>{legacy} (Alt-Position)</option>}
      {positions.map((p) => (
        <option key={p} value={p}>{p}</option>
      ))}
    </select>
  )
}

/** Eine Mitarbeiter-Zeile im Werkstatt-Detail: bearbeiten (Name/Position/E-Mail/Rechte/aktiv),
 *  Passwort zurücksetzen, löschen — alles über den Super-Admin-Pfad (workshopId aus der Route). */
export function WorkshopMemberRow({ workshopId, member }: { workshopId: number; member: AdminWorkshopMember }) {
  const patch = useAdminPatchUser(workshopId)
  const del = useAdminDeleteUser(workshopId)
  const [mode, setMode] = useState<null | 'edit' | 'pw'>(null)

  // Bearbeiten-State (aus dem Member vorbefüllt)
  const [name, setName] = useState(member.name)
  const [position, setPosition] = useState(member.position ?? '')
  const [email, setEmail] = useState(member.email ?? '')
  const [caps, setCaps] = useState<Right[]>(() => cleanRights(member.caps))
  const [pw, setPw] = useState('')

  const isChef = member.role === 'chef'

  function open(next: 'edit' | 'pw') {
    if (next === 'edit') {
      setName(member.name)
      setPosition(member.position ?? '')
      setEmail(member.email ?? '')
      setCaps(cleanRights(member.caps))
    }
    if (next === 'pw') setPw('')
    setMode(next)
  }

  function saveEdit() {
    patch.mutate(
      { userId: member.id, body: { name: name.trim(), position: position.trim(), email: email.trim() || null, permissions: caps } },
      { onSuccess: () => setMode(null) },
    )
  }

  function savePw() {
    if (pw.length < 8) return
    patch.mutate({ userId: member.id, body: { password: pw } }, { onSuccess: () => { setPw(''); setMode(null) } })
  }

  function reactivate() {
    patch.mutate({ userId: member.id, body: { active: true } })
  }

  // „Entfernen" = Soft-Delete (Server setzt active=0, kappt Sitzungen, schützt den letzten Chef).
  // Der Datensatz bleibt für Audit/GoBD erhalten und ist über „Aktivieren" reaktivierbar.
  function remove() {
    if (!window.confirm(`Mitarbeiter „${member.name}" (${member.username}) entfernen?\nDer Zugang wird sofort gesperrt; der Datensatz bleibt für die Nachvollziehbarkeit erhalten.`)) return
    del.mutate(member.id)
  }

  const busy = patch.isPending || del.isPending

  return (
    <div className="wf-card wf-adminrow wf-memberrow">
      <div className="wf-adminrow__head">
        <div className="wf-adminrow__title">
          <strong>{member.name}</strong>
          <span className="wf-muted">@{member.username}</span>
          {isChef && <span className="wf-tag wf-tag--chef">Chef</span>}
          {member.totpEnabled === 1 && <span className="wf-tag">2FA</span>}
          {member.active !== 1 && <span className="wf-tag wf-tag--off">inaktiv</span>}
        </div>
        <div className="wf-adminrow__meta">{member.position || '—'}</div>
      </div>

      <div className="wf-adminrow__actions">
        <button type="button" className="wf-btn wf-btn--sm" onClick={() => (mode === 'edit' ? setMode(null) : open('edit'))} disabled={busy}>
          Bearbeiten
        </button>
        <button type="button" className="wf-btn wf-btn--sm" onClick={() => (mode === 'pw' ? setMode(null) : open('pw'))} disabled={busy}>
          Passwort
        </button>
        {member.active === 1 ? (
          <button type="button" className="wf-btn wf-btn--sm wf-btn--danger" onClick={remove} disabled={busy}
            title={isChef ? 'Den letzten aktiven Chef schützt der Server (kann nicht entfernt werden)' : undefined}>
            Entfernen
          </button>
        ) : (
          <button type="button" className="wf-btn wf-btn--sm wf-btn--primary" onClick={reactivate} disabled={busy}>
            Aktivieren
          </button>
        )}
      </div>

      {mode === 'edit' && (
        <div className="wf-adminrow__edit">
          <div className="two">
            <label className="wf-field">
              <span className="wf-label">Name</span>
              <input className="wf-input" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="wf-field">
              <span className="wf-label">Position</span>
              <PositionSelect value={position} onChange={setPosition} disabled={busy} />
            </label>
          </div>
          <label className="wf-field">
            <span className="wf-label">E-Mail</span>
            <input className="wf-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <span className="wf-label">Rechte{isChef ? ' (Chef hat per Preset ohnehin alle Rechte)' : ''}</span>
          {/* Beim BEARBEITEN überschreibt der Positionswechsel die Rechte NICHT automatisch (kein Datenverlust)
              — nur der Preset-Button setzt sie bewusst. Gleiches Muster wie der Chef-Editor (TeamPage). */}
          <PermissionsMatrix value={caps} onChange={setCaps} grantable={ALL_GRANTABLE} disabled={busy} />
          {patch.isError && <div className="error" role="alert">{(patch.error as Error).message}</div>}
          <div className="wf-form__actions">
            <button type="button" className="wf-btn wf-btn--sm" onClick={() => setCaps(presetRightsForPosition(position))}
              disabled={busy || !(position in RANK)} title={position in RANK ? undefined : 'Für Alt-Positionen gibt es kein Preset'}>
              Auf Positions-Standard setzen
            </button>
            <span className="wf-spacer" />
            <button type="button" className="wf-btn wf-btn--primary wf-btn--sm" onClick={saveEdit} disabled={busy || !name.trim()}>
              Speichern
            </button>
            <button type="button" className="wf-btn wf-btn--sm" onClick={() => setMode(null)} disabled={busy}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {mode === 'pw' && (
        <div className="wf-adminrow__edit">
          <label className="wf-field">
            <span className="wf-label">Neues Passwort (min. 8)</span>
            <input className="wf-input" type="text" value={pw} minLength={8} autoComplete="new-password"
              onChange={(e) => setPw(e.target.value)} />
          </label>
          {patch.isError && <div className="error" role="alert">{(patch.error as Error).message}</div>}
          <div className="wf-form__actions">
            <button type="button" className="wf-btn wf-btn--primary wf-btn--sm" onClick={savePw} disabled={busy || pw.length < 8}>
              Passwort setzen
            </button>
            <button type="button" className="wf-btn wf-btn--sm" onClick={() => setMode(null)} disabled={busy}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {del.isError && <div className="error" role="alert">{(del.error as Error).message}</div>}
    </div>
  )
}
